import { Injectable, OnDestroy } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Router, NavigationEnd } from '@angular/router';
import { filter, first, switchMap, takeUntil, exhaustMap, catchError } from 'rxjs/operators';
import { EMPTY, Subject, from } from 'rxjs';
import { DialogService, Confirmation } from '../../../../../projects/client/src/app/shared/services';

@Injectable({providedIn: 'root'})
export class SwUpdateService implements OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    private swUpdate: SwUpdate,
    private dialogService: DialogService,
    private router: Router
  ) {
    if (!this.swUpdate.isEnabled) return;

    // 1. Wait for the first successful route navigation to finish.
    // This safely replaces appRef.isStable and avoids the setTimeout race condition.
    const initialLoadFinished$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      first()
    );

    // 2. Once the initial load is done, start listening for SW updates.
    initialLoadFinished$.pipe(
      switchMap(() => this.swUpdate.versionUpdates),
      takeUntil(this.destroy$),
      filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'),
      exhaustMap(() => this.promptAndReload$())
    ).subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private promptAndReload$() {
    const confirmation: Confirmation = {
      title: 'Everglades Update',
      message: 'An update to Everglades is available, please click Update to load the new version before continuing.',
      acceptLabel: 'Update',
      acceptButtonType: 'primary',
      rejectButtonType: null,
    };

    return from(
      this.dialogService.confirmAndDo(
        confirmation,
        () => this.activateAndReload()
      )
    ).pipe(
      catchError(() => EMPTY)
    );
  }

  private activateAndReload(): Promise<void> {
    return this.swUpdate.activateUpdate()
      .catch(() => {
      })
      .then(() => window.location.reload());
  }
}
